import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useMemo } from "react";
import authApi from "../apis/authApi";
import { UserContext } from "../context/userContext";
import type { PermissionKey } from "../features/permissions/permissions";
import { hasPermission } from "../helpers/authHelpers";
import type { CurrentUser } from "../models/user";
import { useCableCallback, useCableState } from "./cableHooks";

export const useCurrentUser = () => {
  const queryClient = useQueryClient();
  const { currentUser, isValidating } = useContext(UserContext);
  const handleUserUpdate = useCallback(
    ({ user }: { user: CurrentUser }) => {
      if (currentUser?.id === user.id) {
        queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
      }
    },
    [currentUser?.id, queryClient],
  );
  const channel = useMemo(
    () =>
      currentUser?.id
        ? { channel: "UserChannel", id: currentUser.id }
        : undefined,
    [currentUser?.id],
  );
  useCableCallback(channel, handleUserUpdate);
  return { currentUser, isValidating };
};

export const useWaitingExaminations = () => {
  const { currentUser } = useCurrentUser();
  const channel = useMemo(() => {
    return currentUser?.id
      ? { channel: "WaitingExaminationsChannel", id: currentUser.id }
      : undefined;
  }, [currentUser?.id]);
  const waitingExaminations = useCableState<{ waitingCount: number }>(channel, {
    waitingCount: 0,
  }).waitingCount;
  return waitingExaminations;
};

export const useAuthenticated = () => {
  const authenticated = !!useCurrentUser().currentUser;
  return authenticated;
};

export const usePermission = (permission: PermissionKey) => {
  const permissions = usePermissions();
  return hasPermission(permission, permissions);
};

export const useHasPermission = () => {
  const permissions = usePermissions();
  return (permission: PermissionKey | undefined) =>
    hasPermission(permission, permissions);
};

export const useHasPermissions = () => {
  const userPermissions = usePermissions();
  return (permissions: PermissionKey[]) =>
    permissions.every((permission) =>
      hasPermission(permission, userPermissions),
    );
};

export const useHasAnyPermission = () => {
  const userPermissions = usePermissions();
  return (permissions: PermissionKey[] | undefined) =>
    permissions?.some((permission) =>
      hasPermission(permission, userPermissions),
    ) ?? true;
};

export const usePermissions = () => {
  return useCurrentUser().currentUser?.allPermissions ?? [];
};

export const authKeys = {
  currentUser: ["auth", "currentUser"] as const,
};

export function useValidateToken() {
  return useQuery({
    queryKey: authKeys.currentUser,
    queryFn: () => authApi.currentUser(),
    retry: false,
    staleTime: 60 * 1000, // Consider token valid for 1 minutes
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { username: string; password: string }) =>
      authApi.login(credentials.username, credentials.password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
      queryClient.invalidateQueries({ queryKey: ["ping"] });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser });
    },
  });
}
